<script lang="ts">
import { AbcHeading } from "@ordbogen-npm/abc-dsk";
import { computed, defineComponent, ref } from "vue";
import { useI18n } from "vue-i18n";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import { useVocabAchievementProgress } from "@/common/composables/use-vocab-achievement-progress";
import { useVocabAchievements } from "@/common/composables/use-vocab-achievements";
import BadgeRedemptionModal from "@/components/modals/vocabularyTrainer/BadgeRedemptionModal.vue";
import ClassQuizBaseLayout from "@/components/vocabularyTrainer/ClassQuizBaseLayout.vue";
import EncouragementImage from "@/components/vocabularyTrainer/EncouragementImage.vue";
import QuizJumbotron from "@/components/vocabularyTrainer/QuizJumbotron.vue";
import { useAuthStore, useSettingsStore } from "@/stores";

import AchievementItem from "./AchievementItem.vue";
import ClassQuizPodium from "./ClassQuizPodium.vue";
import { useClassQuizComponent } from "./use-class-quiz-component";

const { commonProps, commonEmits } = useClassQuizComponent();

enum View {
  Encouragement,
  AchievementProgress
}

export default defineComponent({
  components: {
    AchievementItem,
    BadgeRedemptionModal,
    TgButton,
    AbcHeading,
    QuizJumbotron,
    ClassQuizBaseLayout,
    ClassQuizPodium,
    EncouragementImage
  },
  props: commonProps,
  emits: commonEmits,
  setup(props, { emit }) {
    // Import globals
    const authStore = useAuthStore();
    const settingsStore = useSettingsStore();
    const { t } = useI18n();

    const onEndQuiz = () => emit("continue");

    const topPlayers = computed(() => props.classQuiz?.topPlayers || []);

    const { achievementHasMultipleTiers } = useVocabAchievements();

    const {
      sortedAchievements,
      redeemBadge,
      isRedemptionModalOpen,
      loadAchievements,
      onRedeemClick,
      onCloseRedemptionModal,
      onBadgeRedeem
    } = useVocabAchievementProgress();

    const viewState = ref<View>(View.Encouragement);
    const isEncouragementView = computed(() => viewState.value === View.Encouragement);
    const showAchievementProgress = async () => {
      await loadAchievements();

      viewState.value = View.AchievementProgress;
    };

    const encouragementViewHeading = computed(() => {
      const activeLanguageGroup = settingsStore.activeLanguageGroup;
      if (activeLanguageGroup === "english") {
        return t("vocab.classQuiz.end.jumbotronHeading.student.english");
      }

      if (activeLanguageGroup === "german") {
        return t("vocab.classQuiz.end.jumbotronHeading.student.german");
      }

      return "";
    });

    return {
      t,

      userIsTeacher: authStore.userIsTeacher,
      topPlayers,

      viewState,
      isEncouragementView,
      showAchievementProgress,

      achievementHasMultipleTiers,

      sortedAchievements,
      redeemBadge,
      isRedemptionModalOpen,
      onRedeemClick,
      onCloseRedemptionModal,
      onBadgeRedeem,

      onEndQuiz,

      encouragementViewHeading
    };
  }
});
</script>

<template>
  <ClassQuizBaseLayout v-if="userIsTeacher">
    <template #jumbotron>
      <QuizJumbotron has-confetti>
        <div class="jumbotron-inner">
          <AbcHeading tag="h1" size="xl" class="py-4">
            {{ t("vocab.classQuiz.end.jumbotronHeading.teacher") }}
          </AbcHeading>
        </div>
      </QuizJumbotron>
    </template>

    <ClassQuizPodium :top-players="topPlayers" />

    <template #footer>
      <div class="container text-center">
        <TgButton @click="onEndQuiz()">{{ t("vocab.classQuiz.end.next") }}</TgButton>
      </div>
    </template>
  </ClassQuizBaseLayout>

  <ClassQuizBaseLayout v-else-if="isEncouragementView" center-body>
    <template #jumbotron>
      <QuizJumbotron has-confetti>
        <div class="jumbotron-inner">
          <AbcHeading tag="h1" size="xl" class="py-4">
            {{ encouragementViewHeading }}
          </AbcHeading>
        </div>
      </QuizJumbotron>
    </template>

    <div class="container d-flex justify-content-center">
      <div>
        <EncouragementImage class="encouragement-image" />
      </div>
    </div>

    <template #footer>
      <div class="container text-center">
        <TgButton size="md" size-lg="lg" @click="showAchievementProgress()">
          {{ t("vocab.classQuiz.end.next") }}
        </TgButton>
      </div>
    </template>
  </ClassQuizBaseLayout>

  <ClassQuizBaseLayout v-else center-body>
    <template #jumbotron>
      <QuizJumbotron>
        <div class="jumbotron-inner">
          <AbcHeading tag="h1" size="xl" class="py-4">
            {{ t("vocab.classQuiz.end.jumbotronHeading.achievementProgress") }}
          </AbcHeading>
        </div>
      </QuizJumbotron>
    </template>

    <div class="container">
      <div class="row">
        <div class="col-12 col-md-8 offset-md-2 col-lg-6 offset-lg-3">
          <AchievementItem
            v-for="(achievement, index) in sortedAchievements"
            :key="index"
            :achievement="achievement"
            :show-steps-in-name="achievementHasMultipleTiers(achievement)"
            class="mb-4"
            @redeem-click="onRedeemClick(achievement)"
          />
          <BadgeRedemptionModal
            :is-open="isRedemptionModalOpen"
            :badge="redeemBadge"
            @close="onCloseRedemptionModal()"
            @redeem="onBadgeRedeem()"
          />
        </div>
      </div>
    </div>

    <template #footer>
      <div class="container text-center">
        <TgButton size="md" size-lg="lg" tag="router-link" :to="{ name: 'vocab.home' }">
          {{ t("vocab.classQuiz.end.finishQuiz") }}
        </TgButton>
      </div>
    </template>
  </ClassQuizBaseLayout>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.jumbotron-inner {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 245px;
}

.encouragement-image {
  max-width: 175px;
  margin-top: 35px;

  #media-breakpoint-up(md, {
    margin-top: 0;
    max-width: 300px;
  });
}
</style>
